import { NavLink, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { useRBAC } from '@/hooks/useRBAC';
import { ROLES } from '@/utils/rbac';

interface NavItem {
  to: string;
  icon: string;
  label: string;
  end?: boolean;
  permission?: string;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/admin', icon: 'ri-dashboard-3-line', label: '数据总览', end: true, permission: 'dashboard:view' },
  { to: '/admin/events', icon: 'ri-calendar-event-line', label: '活动管理', permission: 'events:view' },
  { to: '/admin/registrations', icon: 'ri-calendar-check-line', label: '活动报名', permission: 'registrations:view' },
  { to: '/admin/applications', icon: 'ri-file-list-3-line', label: '合作申请', permission: 'applications:view' },
  { to: '/admin/guests', icon: 'ri-user-star-line', label: '嘉宾管理', permission: 'guests:view' },
  { to: '/admin/donors', icon: 'ri-heart-line', label: '捐赠人管理', permission: 'donors:view' },
  { to: '/admin/teams', icon: 'ri-user-settings-line', label: '团队成员', permission: 'teams:view' },
  { to: '/admin/milestones', icon: 'ri-footprint-line', label: '成长足迹', permission: 'milestones:view' },
  { to: '/admin/media', icon: 'ri-newspaper-line', label: '媒体报道', permission: 'media:view' },
  { to: '/admin/staff', icon: 'ri-team-line', label: '工作人员', permission: 'staff:view' },
  { to: '/admin/email-config', icon: 'ri-mail-settings-line', label: '邮件配置', permission: 'settings:view' },
  { to: '/admin/subscriptions', icon: 'ri-mail-check-line', label: '订阅管理', permission: 'subscriptions:view' },
];

export default function AdminSidebar() {
  const { logout } = useAdminAuth();
  const { can, currentUser, roles } = useRBAC();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const primaryRole = roles[0];
  const roleDef = primaryRole ? ROLES[primaryRole] : null;

  return (
    <aside className="w-56 min-h-screen bg-[#18181B] flex flex-col shrink-0">
      {/* Logo */}
      <div className="px-5 py-6 border-b border-white/8">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-[#F59E0B] flex items-center justify-center">
            <span className="text-white font-black text-sm">G</span>
          </div>
          <div>
            <div className="text-white font-bold text-sm leading-none">GTRA</div>
            <div className="text-white/40 text-[10px] mt-0.5">管理后台</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5">
        {NAV_ITEMS.map((item) => {
          // Check permission — if no permission needed or user has it, show the item
          const visible = !item.permission || can(item.permission as never);
          if (!visible) return null;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer ${
                  isActive
                    ? 'bg-[#F59E0B]/15 text-[#F59E0B] font-medium'
                    : 'text-white/50 hover:text-white/80 hover:bg-white/5'
                }`
              }
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${item.icon} text-base`} />
              </div>
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      {/* Current User Info */}
      <div className="px-3 py-4 border-t border-white/8">
        <div className="flex items-center gap-3 px-3 py-2.5 mb-1">
          <div className="w-7 h-7 rounded-full bg-[#F59E0B] flex items-center justify-center shrink-0">
            <span className="text-white text-xs font-bold">
              {currentUser?.name?.slice(0, 1) ?? 'A'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white/80 text-xs font-medium truncate">
              {currentUser?.name ?? 'Admin'}
            </div>
            <div className="flex flex-wrap gap-1 mt-0.5">
              {roles.slice(0, 2).map((rk) => (
                <span key={rk} className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold leading-none ${ROLES[rk]?.color ?? 'bg-gray-100 text-gray-600'}`}>
                  {ROLES[rk]?.label ?? rk}
                </span>
              ))}
              {roles.length > 2 && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium leading-none">
                  +{roles.length - 2}
                </span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/40 hover:text-white/70 hover:bg-white/5 text-sm transition-all cursor-pointer"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-logout-box-line text-base" />
          </div>
          退出登录
        </button>
      </div>
    </aside>
  );
}
